/**
 * Exceções de carga (decisões 2 e 9 do Igor): a fábrica conta ambientes, e o
 * PCP marca só o que foge do comum, na aprovação do executivo. Um banheiro
 * pesa metade de uma cozinha; uma cozinha com curva pesa o dobro.
 *
 * A régua oficial continua sendo a CONTAGEM até que as marcas cubram o
 * histórico (plano 5.3): o ritmo das 26 semanas foi medido sem marcas, e
 * aplicar 0,5 aos leves só na fila deixaria a projeção ~15% otimista. Por
 * isso a leitura com pesos mede o ritmo NOS MESMOS PESOS (soma dos pesos dos
 * términos da janela ÷ dias úteis) e só vira a régua oficial, sozinha, quando
 * pelo menos 90% dos términos da janela tiverem marca. Até lá ela aparece
 * como segunda leitura, com a cobertura escrita ao lado.
 *
 * As marcas vêm do backend (`useExcecoesCapacidade`), ligadas pela chave
 * "PEDIDO:AMBIENTE": `{chave: {classe, motivo, observacao, marcado_por,
 * marcado_em, anteriores}}`.
 */
import { normalizeTipoText } from "../tipoAmbiente";
import { CAMADA } from "./classificacao";
import { projetarFila } from "./fila";

export const CLASSE = Object.freeze({ LEVE: "leve", NORMAL: "normal", PESADO: "pesado" });
export const CLASSES = Object.freeze([CLASSE.LEVE, CLASSE.NORMAL, CLASSE.PESADO]);

/** Peso de cada classe na leitura com exceções. Sem marca = normal. */
export const PESO_DA_CLASSE = Object.freeze({ [CLASSE.LEVE]: 0.5, [CLASSE.NORMAL]: 1, [CLASSE.PESADO]: 2 });
export const PESO_SEM_MARCA = PESO_DA_CLASSE[CLASSE.NORMAL];

/** Mesmos códigos do backend (`mapa_producao/excecoes.MOTIVOS`). */
export const MOTIVO_CARGA = Object.freeze({
  CURVA: "curva",
  ITEM_ESPECIAL: "item_especial",
  VOLUME_GRANDE: "volume_grande",
  OUTRO: "outro",
});
export const MOTIVOS_CARGA = Object.freeze(Object.values(MOTIVO_CARGA));
export const CLASSE_QUE_EXIGE_MOTIVO = CLASSE.PESADO;

/** Parte dos términos da janela do ritmo que precisa ter marca para a leitura com pesos virar a oficial. */
export const COBERTURA_MINIMA_DE_MARCAS = 0.9;

/** Teto do lote no backend (tudo ou nada). */
export const LIMITE_DO_LOTE = 300;

export const REGUA = Object.freeze({ CONTAGEM: "contagem", PONDERADA: "ponderada" });

/**
 * Tipos que a análise de 07/10/2026 achou leves (~1 dia de marceneiro, contra
 * ~2 dos demais). Só viram SUGESTÃO na fila "A revisar": nada muda sem o PCP
 * confirmar.
 */
export const TIPOS_SUGERIDOS_LEVES = Object.freeze(["Banheiro", "Lavabo", "Hall", "Área de Serviço"]);
const TIPOS_LEVES_NORMALIZADOS = new Set(TIPOS_SUGERIDOS_LEVES.map(normalizeTipoText));

/** Camadas que o PCP revisa: o executivo já foi aprovado (decisão 9). */
const CAMADAS_A_REVISAR = new Set([CAMADA.APROVADO, CAMADA.EM_PRODUCAO]);

export function pesoDaClasse(classe) {
  return PESO_DA_CLASSE[classe] ?? PESO_SEM_MARCA;
}

/**
 * Classe do item: a que ele traz (ambiente de venda simulada ou assinada,
 * que nasce com classe) ou a marca do PCP pela chave; `null` sem marca.
 */
export function classeDoItem(item, marcas) {
  if (CLASSES.includes(item?.classe)) return item.classe;
  const classe = item?.chave ? marcas?.[item.chave]?.classe : null;
  return CLASSES.includes(classe) ? classe : null;
}

/**
 * O item na fila traz exceção de carga: marca do PCP pela chave (qualquer
 * classe, inclusive "normal" = revisado) ou classe própria fora do normal
 * (venda simulada ou assinada). O "normal" que a venda assinada traz por
 * padrão não conta: ninguém revisou nada e o peso é o mesmo da contagem.
 */
export function temExcecaoNaFila(item, marcas) {
  if (item?.chave && CLASSES.includes(marcas?.[item.chave]?.classe)) return true;
  return CLASSES.includes(item?.classe) && item.classe !== CLASSE.NORMAL;
}

export function pesoDoItem(item, marcas) {
  return pesoDaClasse(classeDoItem(item, marcas));
}

/** Função de peso para `projetarFila` com as marcas dadas. */
export function pesoComMarcas(marcas) {
  return (item) => pesoDoItem(item, marcas);
}

/** "leve" para banheiro, lavabo, hall e área de serviço; `null` nos demais. */
export function sugestaoDeClasse(tipo) {
  return TIPOS_LEVES_NORMALIZADOS.has(normalizeTipoText(tipo)) ? CLASSE.LEVE : null;
}

/** Erro de validação do lote (o mesmo que o backend recusaria), ou `null`. */
export function erroDaMarcacao({ quantidade, classe, motivo }) {
  if (!(quantidade > 0)) return "Escolha ao menos um ambiente.";
  if (quantidade > LIMITE_DO_LOTE) return `Marque no máximo ${LIMITE_DO_LOTE} ambientes por vez.`;
  if (!CLASSES.includes(classe)) return "Escolha leve, normal ou pesado.";
  if (classe === CLASSE_QUE_EXIGE_MOTIVO && !MOTIVOS_CARGA.includes(motivo)) {
    return "Diga por que o ambiente é pesado.";
  }
  return null;
}

function compararEntrega(a, b) {
  if (a.entrega === b.entrega) return 0;
  if (!a.entrega) return 1;
  if (!b.entrega) return -1;
  return a.entrega < b.entrega ? -1 : 1;
}

function compararPedidoEAmbiente(a, b) {
  return (
    String(a.pedido || "").localeCompare(String(b.pedido || ""), "pt-BR", { numeric: true }) ||
    String(a.ambiente || "").localeCompare(String(b.ambiente || ""), "pt-BR")
  );
}

/**
 * Fila "A revisar": abertos aprovados ou em produção ainda sem marca,
 * da entrega mais próxima para a mais distante (sem entrega no fim).
 */
export function filaARevisar({ abertos, marcas }) {
  return (abertos || [])
    .filter((item) => CAMADAS_A_REVISAR.has(item.camada) && item.chave && !marcas?.[item.chave])
    .sort((a, b) => compararEntrega(a, b) || compararPedidoEAmbiente(a, b));
}

/**
 * Ambientes da planilha com marca, `[{...linha, marca}]`, a marcação mais
 * recente primeiro. `classe` filtra (vazio = todas).
 */
export function ambientesMarcados({ linhas, marcas, classe = null }) {
  return (linhas || [])
    .filter((linha) => linha.chave && marcas?.[linha.chave])
    .filter((linha) => !classe || marcas[linha.chave].classe === classe)
    .map((linha) => ({ ...linha, marca: marcas[linha.chave] }))
    .sort(
      (a, b) =>
        String(b.marca.marcado_em || "").localeCompare(String(a.marca.marcado_em || "")) ||
        compararPedidoEAmbiente(a, b),
    );
}

/**
 * Quantos términos do trecho do ritmo têm marca: `{total, marcados, fracao}`.
 * Qualquer classe conta, inclusive "normal" (o PCP revisou e não há exceção).
 */
export function coberturaDeMarcas({ terminos, trecho, marcas }) {
  const naJanela = (terminos || []).filter((t) => trecho && t.termino >= trecho.de && t.termino <= trecho.ate);
  const marcados = naJanela.filter((t) => classeDoItem(t, marcas) !== null).length;
  return { total: naJanela.length, marcados, fracao: naJanela.length ? marcados / naJanela.length : 0 };
}

/**
 * `calcularSegundaLeitura({abertos, terminos, ritmo, marcas, naoUteis, hoje, precisao})`
 * - `ritmo`: retorno de `calcularRitmo` (usa o trecho coberto e os dias úteis dele);
 * - `terminos`: a união de `unirTerminos` (com `chave`).
 *
 * Retorna `{disponivel:false, motivo}` sem ritmo medido, ou
 * `{disponivel:true, oficial, ritmoPonderado, filaPonderada, cobertura,
 * marcadosNaFila, projecao}`. `oficial` = a leitura com pesos substitui a
 * contagem (cobertura ≥ 90%). Sem nenhuma marca a projeção é idêntica à da
 * contagem (todo peso vale 1).
 */
export function calcularSegundaLeitura({ abertos, terminos, ritmo, marcas, naoUteis, hoje, precisao } = {}) {
  if (!ritmo?.ritmo || !ritmo.coberta || !(ritmo.duCoberta > 0)) {
    return { disponivel: false, motivo: ritmo?.motivo || null };
  }
  const trecho = ritmo.coberta;
  const naJanela = (terminos || []).filter((t) => t.termino >= trecho.de && t.termino <= trecho.ate);
  const cargaTerminada = naJanela.reduce((soma, t) => soma + pesoDoItem(t, marcas), 0);
  const ritmoPonderado = cargaTerminada / ritmo.duCoberta;
  const cobertura = coberturaDeMarcas({ terminos, trecho, marcas });
  const peso = pesoComMarcas(marcas);
  const projecao = projetarFila({ abertos, ritmo: ritmoPonderado, naoUteis, hoje, precisao, peso });
  return {
    disponivel: true,
    oficial: cobertura.total > 0 && cobertura.fracao >= COBERTURA_MINIMA_DE_MARCAS,
    ritmoPonderado,
    filaPonderada: projecao.carga,
    cobertura,
    marcadosNaFila: projecao.itens.filter((item) => temExcecaoNaFila(item, marcas)).length,
    projecao,
  };
}
